import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import API_URL from '../config/api';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function PaymentForm({ field, formId, customerEmail, customerName, onPaymentSuccess }) {
  const [clientSecret, setClientSecret] = useState('');
  const [amount, setAmount] = useState(field.paymentAmount || 0);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
        || window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const initializePayment = async () => {
    setLoading(true);
    try {
      const paymentAmount = field.paymentType === 'custom' ? amount : field.paymentAmount;
      
      const response = await axios.post(`${API_URL}/api/stripe/create-payment-intent`, {
        amount: paymentAmount,
        description: field.paymentDescription || field.label,
        customerEmail,
        customerName,
        formId
      });

      setClientSecret(response.data.clientSecret);
    } catch (error) {
      console.error('Payment initialization error:', error);
      alert('Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

   const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#ff6b35',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      borderRadius: '8px',
      // Mobile-specific adjustments
      spacingUnit: isMobile ? '4px' : '5px',
      fontSize: isMobile ? '15px' : '16px',
    }
  };

  const options = {
    clientSecret,
    appearance,
    // Mobile-optimized loader
    loader: 'auto',
  };

   return (
    <div 
      className="payment-field"
      style={{
        padding: isMobile ? '16px' : '20px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9',
      }}
    >
      <div className="payment-info">
        <h3 style={{ 
          fontSize: isMobile ? '18px' : '20px',
          marginBottom: '12px'
        }}>
          {field.label}
        </h3>
        {field.description && (
          <p 
            className="field-description"
            style={{
              fontSize: isMobile ? '14px' : '15px',
              color: '#666',
              marginBottom: '16px'
            }}
          >
            {field.description}
          </p>
        )}
        
        {field.paymentType === 'custom' ? (
          <div className="custom-amount">
            <label style={{ 
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px'
            }}>
              Amount (USD)
            </label>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginTop: '8px' 
            }}>
              <span style={{ 
                fontSize: isMobile ? '20px' : '24px', 
                fontWeight: 'bold' 
              }}>
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={amount / 100}
                onChange={(e) => setAmount(Math.round(parseFloat(e.target.value || 0) * 100))}
                placeholder="Enter amount"
                className="form-input"
                style={{ 
                  fontSize: isMobile ? '16px' : '18px', 
                  padding: isMobile ? '10px' : '12px',
                  width: '100%',
                  maxWidth: '200px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>
        ) : (
          <div className="fixed-amount">
            <div style={{ 
              fontSize: isMobile ? '28px' : '32px', 
              fontWeight: 'bold', 
              color: '#ff6b35', 
              margin: '16px 0' 
            }}>
              ${(field.paymentAmount / 100).toFixed(2)}
            </div>
            <p style={{ 
              color: '#666',
              fontSize: isMobile ? '14px' : '15px'
            }}>
              {field.paymentDescription}
            </p>
          </div>
        )}
      </div>

      {!clientSecret && (
        <button
          type="button"
          onClick={initializePayment}
          disabled={loading || (field.paymentType === 'custom' && amount === 0)}
          className="submit-button"
          style={{ 
            marginTop: '16px',
            width: '100%',
            padding: isMobile ? '14px 20px' : '16px 24px',
            fontSize: isMobile ? '15px' : '16px',
            backgroundColor: (loading || (field.paymentType === 'custom' && amount === 0)) ? '#ccc' : '#0069ff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (loading || (field.paymentType === 'custom' && amount === 0)) ? 'not-allowed' : 'pointer',
            fontWeight: '600'
          }}
        >
          {loading ? 'Initializing...' : 'Continue to Payment'}
        </button>
      )}

      {clientSecret && (
        <Elements options={options} stripe={stripePromise}>
          <CheckoutForm 
            fieldId={field.id}
            onSuccess={onPaymentSuccess}
            isMobile={isMobile}
          />
        </Elements>
      )}
    </div>
  );
}

function CheckoutForm({ fieldId, onSuccess, isMobile }) {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          // Ensure we stay on the same page
          return_url: window.location.href,
        }
      });

      if (error) {
        setMessage(error.message);
        console.error('Payment error:', error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        setMessage('Payment successful! ✓');
        onSuccess(fieldId, {
          paymentIntentId: paymentIntent.id,
          amount: paymentIntent.amount,
          status: 'paid'
        });
      }
    } catch (err) {
      console.error('Payment exception:', err);
      setMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
      <div style={{
        // Add padding for better touch targets on mobile
        padding: isMobile ? '4px' : '0'
      }}>
        <PaymentElement 
          options={{
            layout: {
              type: isMobile ? 'accordion' : 'tabs',
              defaultCollapsed: false,
              radios: false,
              spacedAccordionItems: true
            },
            // Optimize for mobile
            fields: {
              billingDetails: {
                address: {
                  country: 'auto'
                }
              }
            }
          }}
        />
      </div>
      
      <button
        type="submit"
        disabled={isLoading || !stripe || !elements}
        className="submit-button"
        style={{ 
          marginTop: '24px', 
          width: '100%',
          padding: isMobile ? '14px 20px' : '16px 24px',
          fontSize: isMobile ? '15px' : '16px',
          backgroundColor: (isLoading || !stripe || !elements) ? '#ccc' : '#0069ff',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: (isLoading || !stripe || !elements) ? 'not-allowed' : 'pointer',
          fontWeight: '600',
          transition: 'all 0.2s'
        }}
      >
        {isLoading ? 'Processing...' : 'Pay Now'}
      </button>
      
      {message && (
        <div style={{
          marginTop: '16px',
          padding: isMobile ? '10px' : '12px',
          borderRadius: '8px',
          background: message.includes('successful') ? '#d4edda' : '#f8d7da',
          color: message.includes('successful') ? '#155724' : '#721c24',
          fontSize: isMobile ? '14px' : '15px',
          fontWeight: '500'
        }}>
          {message}
        </div>
      )}
    </form>
  );
}

export default PaymentForm;